import { Command } from 'cmdk'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { canSeeAgentJobs } from '@/pages/app/agent-jobs-gate'
import { canSeeWork } from '@/pages/app/work-page'
import { useSearch } from '../api/queries'
import { parsePeriodQuery, periodLabel } from '../period'
import { scopeFromPath } from '../scope'
import { useShell } from '../store'

/**
 * PRD-018 §3.8: one palette for clients, files, pages, work items, people and actions. `>` filters
 * to actions; a month like "aug 2026" offers a period switch. "New task" is PRD-017 §3.13 quick
 * capture: the typed text becomes the literal title, and the row states the defaults it will use
 * (where, when, who) so they are visible before Enter; "New task with details…" opens the form with the
 * same defaults editable (audit P2-17). An empty query never creates an untitled task.
 *
 * Layout note: cmdk's `Command.Dialog` sends `overlayClassName` to the Radix overlay, `contentClassName`
 * to the Radix content (the panel) and `className` to the `[cmdk-root]` div inside the panel. The
 * backdrop and centering live on the overlay/content; the root only stacks its input over its list.
 */
export function Palette({ onNewTask, onNewTaskDetails }: { onNewTask: (title: string) => void, onNewTaskDetails: (title: string) => void }) {
  const { paletteOpen, setPaletteOpen, paletteInitial, bootstrap, setPeriod, toggleTree, setSheetOpen, setNewClientOpen, period } = useShell()
  const [query, setQuery] = useState(paletteInitial)
  const navigate = useNavigate()
  const location = useLocation()
  const q = query.startsWith('>') ? '' : query
  const remote = useSearch(q.length >= 1 ? q : '')
  const actionsOnly = query.startsWith('>')
  const typedPeriod = parsePeriodQuery(query)
  const scope = scopeFromPath(location.pathname)
  const scopeCluster = bootstrap.tree.find(t => t.cluster.id === scope.clusterId)
  const scopeFile = scopeCluster?.files.find(f => f.id === scope.fileId)
  const canEditTemplate = bootstrap.me.role === 'cfo' || bootstrap.me.role === 'controller'

  // Re-seed the query each time the palette opens (`c` opens it empty; a page can open it with a prefill).
  useEffect(() => {
    if (paletteOpen)
      setQuery(paletteInitial) // eslint-disable-line react/set-state-in-effect -- the open event is the trigger; the seed comes from the shell store
  }, [paletteOpen, paletteInitial])

  const go = (href: string) => {
    setPaletteOpen(false)
    navigate(href)
  }
  const local = q
    ? bootstrap.tree.flatMap(t => [
        ...(t.cluster.name.toLowerCase().includes(q.toLowerCase()) ? [{ kind: 'cluster' as const, id: t.cluster.id, title: t.cluster.name, subtitle: t.cluster.structure === 'group' ? 'Client group' : 'Client', href: `/c/${t.cluster.id}` }] : []),
        ...t.files.filter(f => f.name.toLowerCase().includes(q.toLowerCase())).map(f => ({ kind: 'file' as const, id: f.id, title: f.name, subtitle: t.cluster.name, href: `/c/${t.cluster.id}/f/${f.id}` })),
      ])
    : bootstrap.tree.slice(0, 5).map(t => ({ kind: 'cluster' as const, id: t.cluster.id, title: t.cluster.name, subtitle: t.cluster.structure === 'group' ? 'Client group' : 'Client', href: `/c/${t.cluster.id}` }))
  const seen = new Set(local.map(r => r.id))
  const remoteRows = (remote.data ?? []).filter(r => !seen.has(r.id))
  const pages = [
    { id: 'home', title: 'Home', href: '/home' },
    { id: 'my_work', title: 'My work', href: '/my-work' },
    // PRD-018 v0.4 §3.18: Work, CFO and Controller.
    ...(canSeeWork(bootstrap.me.role) ? [{ id: 'work', title: 'Work', href: '/work' }] : []),
    // PRD-016 §5 v2.6.2: Agent jobs, CFO and Controller only (the same predicate as the tree row and the route gate).
    ...(canSeeAgentJobs(bootstrap.me.role) ? [{ id: 'threads', title: 'Agent jobs', href: '/agent-jobs' }] : []),
    { id: 'team', title: 'Team', href: '/team' },
    { id: 'settings', title: 'Settings', href: '/settings/general' },
  ]
  const newTaskTitle = q.trim()
  const where = scopeFile ? `${scopeCluster!.cluster.name} · ${scopeFile.name}` : scopeCluster ? scopeCluster.cluster.name : 'a client you pick'
  const defaults = `in ${where} · ${periodLabel(period)} · due at the close target · assigned to you`

  return (
    <Command.Dialog
      open={paletteOpen}
      onOpenChange={o => setPaletteOpen(o)}
      label="Command palette"
      shouldFilter={!actionsOnly}
      overlayClassName="fixed inset-0 z-50 bg-overlay/30"
      contentClassName="fixed left-1/2 top-[12vh] z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-secondary bg-primary shadow-xl outline-none"
      className="flex flex-col"
      data-testid="palette"
    >
      <Command.Input value={query} onValueChange={setQuery} placeholder="Search clients, files, work items… or type > for actions" autoFocus className="w-full border-b border-secondary bg-transparent px-4 py-3 text-sm text-primary outline-none placeholder:text-placeholder" data-testid="palette-input" />
      <Command.List className="max-h-[60vh] overflow-y-auto p-2 text-sm">
        <Command.Empty className="px-3 py-6 text-center text-tertiary">Nothing matches. Try a client or file name, or type &gt; for actions.</Command.Empty>
        {typedPeriod
          ? (
              <Command.Group heading="Period">
                <Item
                  onSelect={() => {
                    setPeriod(typedPeriod)
                    setPaletteOpen(false)
                  }}
                  title={`Switch period to ${periodLabel(typedPeriod)}`}
                  value={`switch period ${query}`}
                />
              </Command.Group>
            )
          : null}
        {!actionsOnly
          ? (
              <>
                <Command.Group heading="Clients and files">
                  {local.map(r => <Item key={r.id} value={`${r.title} ${r.subtitle}`} title={r.title} subtitle={r.subtitle} onSelect={() => go(r.href)} />)}
                  {remoteRows.filter(r => r.kind === 'cluster' || r.kind === 'file').map(r => <Item key={r.id} value={`${r.title} ${r.subtitle ?? ''}`} title={r.title} subtitle={r.subtitle} onSelect={() => go(r.href)} />)}
                </Command.Group>
                <Command.Group heading="Pages">
                  {pages.map(p => <Item key={p.id} value={p.title} title={p.title} onSelect={() => go(p.href)} />)}
                </Command.Group>
                {remoteRows.some(r => r.kind === 'item')
                  ? (
                      <Command.Group heading="Work items">
                        {remoteRows.filter(r => r.kind === 'item').map(r => <Item key={r.id} value={`${r.title} ${r.subtitle ?? ''}`} title={r.title} subtitle={r.subtitle} onSelect={() => go(r.href)} />)}
                      </Command.Group>
                    )
                  : null}
                {remoteRows.some(r => r.kind === 'person')
                  ? (
                      <Command.Group heading="People">
                        {remoteRows.filter(r => r.kind === 'person').map(r => <Item key={r.id} value={`${r.title} ${r.subtitle ?? ''}`} title={r.title} subtitle={r.subtitle} onSelect={() => go(r.href)} />)}
                      </Command.Group>
                    )
                  : null}
              </>
            )
          : null}
        <Command.Group heading="Actions">
          {newTaskTitle
            ? (
                <Item
                  value={`new task ${newTaskTitle}`}
                  title={`New task: “${newTaskTitle}”`}
                  subtitle={defaults}
                  onSelect={() => {
                    setPaletteOpen(false)
                    onNewTask(newTaskTitle)
                  }}
                  testId="palette-new-task"
                />
              )
            : null}
          <Item
            value={`new task with details ${newTaskTitle}`}
            title={newTaskTitle ? 'New task with details…' : 'New task…'}
            subtitle={newTaskTitle ? 'Change the client, file, due date or assignee first' : 'Title, client, file, due date, assignee'}
            onSelect={() => {
              setPaletteOpen(false)
              onNewTaskDetails(newTaskTitle)
            }}
            testId="palette-new-task-details"
          />
          {scopeCluster && canEditTemplate
            ? <Item value={`edit close template ${scopeCluster.cluster.name}`} title={`Edit close template · ${scopeCluster.cluster.name}`} subtitle="Rows, cadence, assignees, dependencies" onSelect={() => go(`/c/${scopeCluster.cluster.id}/template`)} testId="palette-template" />
            : null}
          {bootstrap.capabilities.canCreateClient
            ? (
                <Item
                  value="new client"
                  title="New client…"
                  subtitle="Name, structure, account lead"
                  onSelect={() => {
                    setPaletteOpen(false)
                    setNewClientOpen(true)
                  }}
                  testId="palette-new-client"
                />
              )
            : null}
          <Item
            value="collapse tree"
            title="Collapse or expand the tree"
            subtitle="⌘\\"
            onSelect={() => {
              toggleTree()
              setPaletteOpen(false)
            }}
          />
          <Item
            value="keyboard shortcuts"
            title="Keyboard shortcuts"
            subtitle="?"
            onSelect={() => {
              setPaletteOpen(false)
              setSheetOpen(true)
            }}
          />
        </Command.Group>
      </Command.List>
    </Command.Dialog>
  )
}

function Item({ title, subtitle, onSelect, value, testId }: { title: string, subtitle?: string | null, onSelect: () => void, value: string, testId?: string }) {
  return (
    <Command.Item value={value} onSelect={onSelect} data-testid={testId} className="flex cursor-pointer items-center justify-between gap-3 rounded-md px-3 py-2 text-primary data-[selected=true]:bg-secondary">
      <span className="truncate">{title}</span>
      {subtitle ? <span className="shrink-0 truncate text-xs text-quaternary">{subtitle}</span> : null}
    </Command.Item>
  )
}
